/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {
  AlertAction,
  AlertActionTemplate,
  AlertActionTemplates,
  AlertConfiguration,
  AlertNotification,
  AssetType,
  GenericObject,
  Link,
  MessageType
} from '../types/entities';
import {getSingleNotificationType} from './Connect';
import {entityId, getExtendedAttributeValue} from './ConnectUtils';

/**
 * The utility helps the clients to determine whether the
 * alert is from device or not. This is identified based on the
 * event type or asset type.
 *
 * ```typescript
 *
 *  import {isDeviceAlert} from '@ion/core/src/services/AlertUtils';
 *
 *  const isSCIDevice = isDeviceAlert(alert);
 *
 * ```
 * @param alert The alert to determine
 * @returns true if alert is from device, else false
 */
export const isDeviceAlert = (alert: GenericObject) => {
  const {avlEventType, assetType} = alert;
  return (
    avlEventType === MessageType.LOCO_EVENT ||
    avlEventType === MessageType.TAG_REPORT ||
    assetType === AssetType.ScDevice
  );
};

/**
 * The utility helps the clients to determine whether the
 * language of alert config is en or something else.
 *
 * ```typescript
 *
 *  import {isAlertLanguageEn} from '@ion/core/src/services/AlertUtils';
 *
 *  const isAlertEn = isAlertLanguageEn(alert);
 *
 * ```
 * @param alert The alert to determine
 * @returns true if language prop has been set in alert config extended attribute & it's value is 'en'
 *               Also returns true if alert config doesn't have extended attribute set i.e. default = 'en'
 */
export const isAlertLanguageEn = (alert: AlertNotification): boolean => {
  const language = getExtendedAttributeValue(alert, 'language');
  return language ? language === 'en' : true;
};

/**
 * The utility helps the clients to determine whether the given alert type if for crashboxx and type EMAIL
 *
 * ```typescript
 *
 *  import {getCrashBoxEmailTemplate} from '@ion/core/src/services/AlertUtils';
 *
 *  const templateName = getCrashBoxEmailTemplate(alertType, action);
 *
 * ```
 * @param alertType The alertType hateos object contained within the alert config entity
 * @param action The Alert Action object
 * @returns If alert type was of crashboxx and action EMAIL, then returns the iOn crashboxx email template name
 */
export const getCrashBoxEmailTemplate = (alertType: Link, action: AlertAction): string | undefined => {
  if (alertType?.title === 'FNL') {
    if (action?.actionType === 'EMAIL') {
      return 'ION-CRASHBOXX-EMAIL-TEMPLATE-EN';
    }
  }
};

/**
 * The utility helps the clients to retrieve the default alert template name from alert type
 *
 * ```typescript
 *
 *  import {getDefaultTemplateName} from '@ion/core/src/services/AlertUtils';
 *
 *  const templateName = getDefaultTemplateName(alertType, action);
 *
 * ```
 * @param alertType The alertType hateos object contained within the alert config entity
 * @param action The Alert Action object
 * @returns Returns the template name by retrieving alert type entity and filtering for action type
 */
export const getDefaultTemplateName = async(alertType: Link, action: AlertAction): Promise<string> => {
  //Get action templates from alert type
  const {actionTemplates} = await getSingleNotificationType(entityId(alertType));
  const defaultTemplate = actionTemplates?.find(template => template?.title?.includes(`-${action?.actionType}-`));
  return defaultTemplate?.title;
};

/**
 * Filter a list of templates for given language and return the template hateos object.
 * When templates were retrieved but none match the given language, the caller's
 * selected alertActionTemplate is kept rather than clearing out a valid selection.
 *
 * @param templates the list of alert action templates
 * @param language the language of the template
 * @param selected the alertActionTemplate already set on the action by the caller
 * @returns the alertActionTemplate hateos object, otherwise null
 */
const createAlertTemplateForLanguage = (
  templates?: AlertActionTemplate[],
  language?: string,
  selected?: Link
) => {
  if (templates?.length) {
    const template = templates?.find(template => template?.localeld === language);
    return template ? {
      href : template?._self?.href,
      rel  : 'actionTemplate'
    } : (selected || null);
  }
  return null;
};

/**
 * Generate alert action name using alert name, action type and latest timestamp.
 *
 * @param alert the alert config entity
 * @param action the alert action entity
 * @returns the name of alert action entity
 */
export const createAlertActionName = (alert: AlertConfiguration, action: AlertAction): string => (
  `${alert?.name}_${action?.actionType}_${new Date().getTime()}`
);

/**
 * The utility helps the clients to generate the alert action entity that has to be created
 * for a given alert config and templates
 *
 * ```typescript
 *
 *  import {updateAlertActionEntity} from '@ion/core/src/services/AlertUtils';
 *
 *  const action = updateAlertActionEntity({alert, action, templates}, true/false);
 *
 * ```
 * @param AlertActionTemplates This object consists of three attributes - alert, action and templates
 *   alert - alert config object
 *   action - alert action object
 *   templates - List of alert templates retrieved based on language
 *     If language is en, then tempalates isn't passed
 * @param isEdit This is optional param and passed in to indicate whether we're performing create/edit operation
 * @returns Returns the updated action entity which'll have updated name, alertActionTemplate based on language
 */
export const updateAlertActionEntity = ({
  alert,
  action,
  templates
}: AlertActionTemplates, isEdit = false): AlertAction => {
  const language = getExtendedAttributeValue(alert, 'language');
  action.name = createAlertActionName(alert, action);
  action.alertConfiguration = alert?._self;
  action.alertActionTemplate = createAlertTemplateForLanguage(templates, language, action.alertActionTemplate);
  action.account = alert.account;
  if (!isEdit) {
    delete action.id;
    delete action._self;
  }
  return action;
};
